import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Surface, Button, Badge, Input, Select, Grid, StatCard, PageHeader } from '../../ui/components';
import { RefreshCw, Eye } from 'lucide-react';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';
import { supabase } from '../../lib/supabase';

const statusColors = {
  in_process: 'primary',
  clear_to_close: 'warning',
  closed: 'success',
  cancelled: 'danger',
};

const statusLabels = {
  in_process: 'In Process',
  clear_to_close: 'Clear to Close',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

const LenderPortfolio = () => {
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [filteredLoans, setFilteredLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({
    totalLoans: 0,
    totalVolume: 0,
    inProcess: 0,
    closed: 0,
    avgLoanSize: 0,
  });

  useEffect(() => {
    fetchPortfolio();
  }, []);

  useEffect(() => {
    filterLoans();
  }, [loans, searchTerm, statusFilter]);

  const fetchPortfolio = async () => {
    setLoading(true);
    try {
      // Fetch loans where lender's bid was accepted
      const { data, error } = await supabase
        .from('lender_bids')
        .select('*, loan_applications(id, anonymous_id, loan_amount, product_type, readiness_score, created_at, status)')
        .eq('status', 'accepted')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const portfolioLoans = (data || []).map(bid => ({
        ...bid,
        loan_status: bid.loan_applications?.status || 'in_process',
        loan_id: bid.loan_applications?.id,
        anonymous_id: bid.loan_applications?.anonymous_id,
        loan_amount: bid.loan_applications?.loan_amount,
        product_type: bid.loan_applications?.product_type,
        readiness_score: bid.loan_applications?.readiness_score,
        application_date: bid.loan_applications?.created_at,
      }));

      setLoans(portfolioLoans);

      // Calculate stats
      const totalVolume = portfolioLoans.reduce((sum, loan) => sum + (loan.loan_amount || 0), 0);
      const inProcess = portfolioLoans.filter(l => ['in_marketplace', 'bid_accepted'].includes(l.loan_status)).length;
      const closed = portfolioLoans.filter(l => l.loan_status === 'closed').length;

      setStats({
        totalLoans: portfolioLoans.length,
        totalVolume,
        inProcess,
        closed,
        avgLoanSize: portfolioLoans.length > 0 ? totalVolume / portfolioLoans.length : 0,
      });
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      setLoans([]);
    } finally {
      setLoading(false);
    }
  };

  const filterLoans = () => {
    let filtered = loans;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(loan => loan.loan_status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(loan =>
        loan.anonymous_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        loan.loan_id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredLoans(filtered);
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString()}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatRate = (rate) => {
    return `${(rate || 0).toFixed(3)}%`;
  };

  const calculateMonthlyPayment = (loanAmount, rate, termYears = 30) => {
    const principal = loanAmount;
    const monthlyRate = rate / 100 / 12;
    const numPayments = termYears * 12;
    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
    return payment;
  };

  const getDaysInPortfolio = (date) => {
    const now = new Date();
    const accepted = new Date(date);
    const diffDays = Math.floor((now - accepted) / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const closeRate = stats.totalLoans > 0 ? Math.round((stats.closed / stats.totalLoans) * 100) : 0;

  return (
    <div>
      <PageHeader
        title="Portfolio"
        description="Manage your active and closed loans"
        actions={<Button variant="primary" icon={RefreshCw} onClick={fetchPortfolio}>Refresh</Button>}
      />

      {/* Stats Cards */}
      <Grid cols={4} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
        <StatCard label="Total Loans" value={stats.totalLoans} />
        <StatCard label="Total Volume" value={formatCurrency(stats.totalVolume)} />
        <StatCard label="In Process" value={stats.inProcess} />
        <StatCard label="Closed" value={stats.closed} />
      </Grid>

      {/* Performance Metrics */}
      <Grid cols={3} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
        <StatCard label="Avg Loan Size" value={formatCurrency(stats.avgLoanSize)} />
        <StatCard label="Close Rate" value={`${closeRate}%`} />
        <Surface padding="var(--lk-space-4)">
          <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--lk-space-2)' }}>Pipeline Health</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
            <div style={{ flex: 1, height: 8, borderRadius: 4, backgroundColor: 'var(--lk-gray-100)' }}>
              <div style={{ height: '100%', width: `${closeRate}%`, borderRadius: 4, backgroundColor: 'var(--lk-success-500)', transition: 'width 0.6s ease' }} />
            </div>
            <span style={{ fontSize: 'var(--lk-text-sm)', fontWeight: 600 }}>{closeRate}%</span>
          </div>
        </Surface>
      </Grid>

      {/* Filters */}
      <Surface padding="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'var(--lk-space-3)', alignItems: 'center' }}>
          <Input
            placeholder="Search by loan ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="in_marketplace">In Process</option>
            <option value="bid_accepted">Accepted</option>
            <option value="closed">Closed</option>
            <option value="cancelled">Cancelled</option>
          </Select>
          <div style={{ textAlign: 'right', color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>
            Showing {filteredLoans.length} of {loans.length}
          </div>
        </div>
      </Surface>

      {/* Portfolio Table */}
      <Surface padding="0" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="lk-table">
            <thead>
              <tr>
                <th>Loan ID</th>
                <th>Product</th>
                <th style={{ textAlign: 'right' }}>Loan Amount</th>
                <th style={{ textAlign: 'right' }}>Your Rate</th>
                <th style={{ textAlign: 'right' }}>Monthly Payment</th>
                <th>Accepted Date</th>
                <th>Days in Portfolio</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 'var(--lk-space-8)' }}>
                    <div className="lk-spinner lk-spinner-sm" style={{ display: 'inline-block', marginRight: 8 }} />
                    Loading portfolio...
                  </td>
                </tr>
              ) : filteredLoans.length > 0 ? (
                filteredLoans.map((loan) => {
                  const monthlyPayment = calculateMonthlyPayment(loan.loan_amount, loan.interest_rate);
                  const daysInPortfolio = getDaysInPortfolio(loan.created_at);

                  return (
                    <tr key={loan.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                        {loan.anonymous_id || `LK-${loan.loan_id?.slice(0, 8).toUpperCase()}`}
                      </td>
                      <td>
                        <Badge variant="neutral">
                          {loan.product_type?.replace(/_/g, ' ') || '-'}
                        </Badge>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(loan.loan_amount)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--lk-primary-600)' }}>{formatRate(loan.interest_rate)}</td>
                      <td style={{ textAlign: 'right', color: 'var(--lk-text-tertiary)' }}>{formatCurrency(monthlyPayment)}/mo</td>
                      <td style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{formatDate(loan.created_at)}</td>
                      <td>
                        <Badge variant={daysInPortfolio > 45 ? 'warning' : 'neutral'}>
                          {daysInPortfolio}d
                        </Badge>
                      </td>
                      <td>
                        <Badge variant={statusColors[loan.loan_status] || 'primary'}>
                          {statusLabels[loan.loan_status] || loan.loan_status?.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={Eye}
                          onClick={() => navigate(`/lender/loan/${loan.loan_id}`)}
                        />
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', color: 'var(--lk-text-quaternary)', padding: 'var(--lk-space-8)' }}>
                    {searchTerm || statusFilter !== 'all'
                      ? 'No loans match your filters'
                      : 'Your portfolio is empty. Win bids in the Marketplace to build your portfolio!'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Surface>

      <FloatingOwenAssistant context="lender-portfolio" />
      <FloatingKnowledge customContent={{
        label: 'Portfolio',
        title: 'Portfolio management',
        body: 'Track and manage your loan portfolio.',
        bullets: ['Monitor loan performance', 'Track funding status', 'Manage servicing'],
        resources: [{ label: 'Portfolio guide', href: '#' }],
      }} />
    </div>
  );
};

export default LenderPortfolio;
